/**
 * useAutoFollow.ts
 * While a session is live, polls the current slide and automatically pushes
 * that slide's activity to the room so students' views follow the teacher
 * through the deck — no need to click "Start Presenting" on every slide.
 */

import { useEffect, useRef } from 'react';
import { getCurrentSlideIndex, getSlideImageBase64 } from '@office/slideService';
import { loadSlideConfig, getStoredSlideId } from '@office/settingsService';
import { updateActiveSlide, updateRoomStatus } from '@db/roomService';
import { pushSlideImage } from '@db/submissionService';

const POLL_MS = 1200;

export function useAutoFollow(roomCode: string | null, enabled: boolean, mirrorSlides = false): void {
  const lastSlide = useRef<number | null>(null);

  useEffect(() => {
    if (!roomCode || !enabled) return;
    lastSlide.current = null; // re-push current slide when (re)enabled

    let stopped = false;
    const tick = async () => {
      try {
        const slideIndex = await getCurrentSlideIndex();
        if (stopped || slideIndex === lastSlide.current) return;
        lastSlide.current = slideIndex;

        const config = loadSlideConfig(slideIndex);
        if (config && config.type === 'video_quiz') {
          // Video quizzes are teacher-driven: the teacher taps each question
          // in the Video Presenter, so don't auto-push the video config to
          // students — leave them waiting until a question is asked.
          await updateActiveSlide(roomCode, `slide_${slideIndex}`, null);
          await updateRoomStatus(roomCode, 'waiting');
        } else if (config) {
          const slideId = getStoredSlideId(slideIndex) ?? `slide_${slideIndex}`;
          await updateActiveSlide(roomCode, slideId, config);
          await updateRoomStatus(roomCode, 'active');
        } else {
          // Slide has no activity — mirror the slide image so students can
          // still view it, and put them on the viewing/waiting screen.
          await updateActiveSlide(roomCode, `slide_${slideIndex}`, null);
          await updateRoomStatus(roomCode, 'waiting');
          // Slide mirroring is a Premium+ feature.
          if (mirrorSlides) {
            const img = await getSlideImageBase64(slideIndex);
            if (img && !stopped) await pushSlideImage(roomCode, slideIndex, img);
          }
        }
      } catch (err) {
        console.warn('[GuroHub] auto-follow tick failed:', err);
      }
    };

    const interval = setInterval(tick, POLL_MS);
    void tick();
    return () => { stopped = true; clearInterval(interval); };
  }, [roomCode, enabled, mirrorSlides]);
}
